import { logger } from '../utils/logger';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useNotifications } from '../contexts/NotificationContext';
import {
  Palette,
  Upload,
  Loader2,
  Check,
  Image as ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';

const PRESET_COLORS = [
  '#4B6651', '#3E5543', '#2A7D4F', '#1B5E3B',
  '#C06144', '#A84F38', '#8B3A2A', '#D4725C',
  '#2C5282', '#2B6CB0', '#3182CE', '#1A365D',
  '#553C9A', '#6B46C1', '#805AD5', '#44337A',
  '#2A2C2A', '#5C5F5C', '#744210', '#975A16'
];

const ClinicBranding = ({ clinicId, isAdmin }) => {
  const [displayName, setDisplayName] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#4B6651');
  const [logoUrl, setLogoUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const { refresh } = useNotifications();
  const fullLogoUrl = logoUrl ? (logoUrl.startsWith('http') ? logoUrl : `${BACKEND_URL}${logoUrl}`) : '';

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/clinics/${clinicId}/branding`, { withCredentials: true });
        setDisplayName(data.display_name || data.name || '');
        setPrimaryColor(data.primary_color || '#4B6651');
        setLogoUrl(data.logo_url || '');
      } catch (error) {
        logger.error('Error fetching branding:', error);
      } finally {
        setLoading(false);
      }
    };
    if (clinicId) fetchBranding();
  }, [clinicId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/clinics/${clinicId}/branding`, {
        display_name: displayName,
        primary_color: primaryColor
      }, { withCredentials: true });
      toast.success('Branding updated');
      refresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(
        `${BACKEND_URL}/api/clinics/${clinicId}/logo`,
        formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setLogoUrl(data.logo_url);
      toast.success('Logo uploaded');
      refresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  if (loading) {
    return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#4B6651]" /></div>;
  }

  if (!isAdmin) {
    return (
      <Card className="border-[#E5E4E0] bg-white">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            {fullLogoUrl && (
              <img src={fullLogoUrl} alt="Clinic logo" className="w-12 h-12 object-contain rounded" />
            )}
            <div>
              <p className="font-medium text-[#2A2C2A]">{displayName || 'No display name set'}</p>
              <div className="flex items-center gap-2 mt-1">
                <div className="w-4 h-4 rounded" style={{ backgroundColor: primaryColor }} />
                <span className="text-xs text-[#5C5F5C]">{primaryColor}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-[#E5E4E0] bg-white" data-testid="clinic-branding">
      <CardHeader>
        <CardTitle className="text-base font-['Work_Sans'] flex items-center gap-2">
          <Palette className="w-5 h-5 text-[#4B6651]" />
          Clinic Branding
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Logo */}
        <div>
          <label className="text-xs text-[#5C5F5C] font-medium mb-2 block">Logo</label>
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 border-2 border-dashed border-[#E5E4E0] rounded-lg flex items-center justify-center bg-[#F8F7F4] overflow-hidden">
              {fullLogoUrl ? (
                <img src={fullLogoUrl} alt="Logo" className="w-full h-full object-contain" data-testid="clinic-logo-preview" />
              ) : (
                <ImageIcon className="w-8 h-8 text-[#5C5F5C] opacity-40" />
              )}
            </div>
            <label className="cursor-pointer">
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
                data-testid="logo-upload-input"
              />
              <Button variant="outline" size="sm" className="border-[#E5E4E0]" disabled={uploading} asChild>
                <span>
                  {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                  {logoUrl ? 'Change Logo' : 'Upload Logo'}
                </span>
              </Button>
            </label>
          </div>
        </div>

        {/* Display Name */}
        <div>
          <label className="text-xs text-[#5C5F5C] font-medium mb-1 block">Display Name</label>
          <Input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Your clinic's display name"
            className="border-[#E5E4E0]"
            data-testid="clinic-display-name-input"
          />
          <p className="text-xs text-[#5C5F5C] mt-1">Shown in sidebar, emails, and PDF exports</p>
        </div>

        {/* Primary Color */}
        <div>
          <label className="text-xs text-[#5C5F5C] font-medium mb-2 block">Primary Color</label>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg border-2 border-[#E5E4E0]" style={{ backgroundColor: primaryColor }} />
            <Input
              type="text"
              value={primaryColor}
              onChange={(e) => setPrimaryColor(e.target.value)}
              className="w-28 border-[#E5E4E0] font-mono text-sm"
              data-testid="clinic-color-input"
            />
            <input
              type="color"
              value={primaryColor}
              onChange={(e) => setPrimaryColor(e.target.value)}
              className="w-10 h-10 cursor-pointer rounded border-0"
              data-testid="clinic-color-picker"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {PRESET_COLORS.map((color) => (
              <button
                key={color}
                onClick={() => setPrimaryColor(color)}
                className={`w-7 h-7 rounded-md border-2 transition-transform hover:scale-110 ${
                  primaryColor === color ? 'border-[#2A2C2A] scale-110' : 'border-transparent'
                }`}
                style={{ backgroundColor: color }}
                title={color}
              />
            ))}
          </div>
        </div>

        {/* Preview */}
        <div>
          <label className="text-xs text-[#5C5F5C] font-medium mb-2 block">Preview</label>
          <div className="border border-[#E5E4E0] rounded-lg overflow-hidden">
            <div className="p-3 flex items-center gap-3" style={{ backgroundColor: primaryColor }}>
              {fullLogoUrl ? (
                <img src={fullLogoUrl} alt="" className="w-8 h-8 object-contain rounded" />
              ) : (
                <div className="w-8 h-8 bg-white/20 rounded flex items-center justify-center">
                  <span className="text-white text-sm font-bold">{displayName?.charAt(0) || 'V'}</span>
                </div>
              )}
              <span className="text-white font-medium text-sm">{displayName || 'Clinic Name'}</span>
            </div>
            <div className="p-3 bg-white">
              <p className="text-xs text-[#5C5F5C]">This is how your clinic appears in the sidebar and email headers.</p>
            </div>
          </div>
        </div>

        <Button
          onClick={handleSave}
          disabled={saving}
          className="text-white"
          style={{ backgroundColor: primaryColor }}
          data-testid="save-branding-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
          Save Branding
        </Button>
      </CardContent>
    </Card>
  );
};

export default ClinicBranding;
